import axios from "axios";
import { TimeCell } from "../model/timeCell";
import { GuestInfo } from "../model/guest-info";
import moment, { Moment } from "moment-timezone";

const GUEST_BOOKINGS_URL = "./services/apexrest/getBookingsByNeighbor";
const BOOKING_URL = "./services/apexrest/bookAppointments";
const CANCEL_BOOKING_URL = "./services/apexrest/cancelAppointment";
const CANCEL_APPOINTMENTS_URL = "./services/apexrest/cancelAppointments";
const QUERY_URL = "./services/data/v48.0/query";

export const STATUS_SCHEDULED = "Scheduled";
export const STATUS_CANCELED = "Canceled";
export const STATUS_ATTENDED = "Attended";

export interface PropertyListItem {
  propertyName: string;
  tourType: string;
  tourTime: Date;
}

export interface BookingRequest {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  attributionId?: string;
  allowSMSConfirmation?: string;
  desiredMoveInDate?: string;
  minBedrooms?: string;
  minBathrooms?: string;
  bookedById?: string;
  callTrxId?: string;
  requestSource?: string;
  webClientId: string;
  tourNotes?: string;
  tourAction?: string;
  bookingRefName?: string;
  propertyList: PropertyListItem[];
  callingMode?: string;
}

export interface BookingResponse {
  status: string;
  message: string;
  guestCardId: string;
  confirmationNumber: string;
}

export interface GuestBooking {
  status: string;
  confirmationNumber: string;
  guestCardId: string;
  propertyId: string;
  propertyName: string;
  tourType: string;
  startTime: Moment;
  endTime: Moment;
}

export interface AppointmentItem {
  status: string;
  tourType: string;
  tourStartTime: Date;
  propertyName: string;
  message: string;
  error: string;
  appointmentNumber: string;
  tourDateDisplay: string;
  tourDateFormatted: string;
  tourStartTimeDisplay: string;
  tourStartTimeFormatted: string;
  tourEndTimeDisplay: string;
  apartmentTypeDisplay: string;
  tourTypeDisplay: string;
  neighborhoodAddress: string;
  neighborhoodAddressL1: string;
  neighborhoodAddressL2: string;
  tourDescription: string;
  tourInstructions: string;
  officeHoursLine1: string;
  officeHoursLine2: string;
  officeHoursLine3: string;
  neighborhoodPhone: string;
  neighborhoodMarket: string;
}

export interface AppointmentsData {
  email: string;
  contactId: string;
  bookingList: AppointmentItem[];
}

export interface AppointmentsResponse {
  status: number;
  statusText: string;
  data: AppointmentsData;
}

export interface AppointmentCancelItem {
  appointmentNumber: string;
  cancelReason: string;
}

export interface AppointmentCancelRequest {
  appointmentsList: AppointmentCancelItem[];
}

export interface GuestBookingsResponse {
  status: number;
  statusText: string;
  data: GuestBookingData[];
}

export interface GuestBookingData {
  attributes: Attributes;
  Id: string;
  Name: string;
  Tour_Start_Time__c: string;
  Tour_End_Time__c?: string;
  Tour_Type__c?: string;
  Status__c: string;
  Service_Territory_Name__c: string;
  Contact__c: string;
  Contact__r: Contact;
  Account__c: string;
  Account__r: Account;
}

export interface Attributes {
  type: string;
  url: string;
}

export interface Account {
  attributes: Attributes;
  Id: string;
  Name: string;
}

export interface Contact {
  attributes: Attributes;
  Id: string;
  FirstName: string;
  LastName: string;
  Email: string;
  Phone: string;
  pDesired_Move_In_Date__c?: Date;
  Number_of_Beds__c?: string;
  Number_of_Baths__c?: string;
  pAttribution_ID__c?: string;
  HasOptedOutOfEmail?: boolean;
  HasOptedOutOfSMS__c?: boolean;
}

export interface GuestRecord {
  Contact__r: {
    attributes: Attributes;
    FirstName: string;
    LastName: string;
    Phone: string;
    Email: string;
    pDesired_Move_In_Date__c: string;
    Number_of_Beds__c: string;
    Number_of_Baths__c: string;
    pAttribution_ID__c: string;
    HasOptedOutOfEmail: boolean;
    HasOptedOutOfSMS__c: boolean;
  };
  Id: string;
  RealPage_Guest_Card_Id__c: string;
  Lead_Source__c: string;
}

export interface GuestRecordResponse {
  totalSize: number;
  done: boolean;
  records: GuestRecord[];
}

export class CalendarBookingResponse implements BookingResponse {
  public status: string;
  public message: string;
  public guestCardId: string;
  public confirmationNumber: string;
  public timecell: TimeCell;

  constructor(response: BookingResponse, timecell: TimeCell) {
    this.status = response.status;
    this.message = response.message;
    this.guestCardId = response.guestCardId;
    this.confirmationNumber = response.confirmationNumber;
    this.timecell = timecell;
  }
}

export class BookingService {
  bookingRequest(guestInfo: GuestInfo, timecell: TimeCell, webClientId: string): BookingRequest {
    const bookingRequest = {
      firstName: guestInfo.firstName,
      lastName: guestInfo.lastName,
      email: guestInfo.emailAddress,
      phone: guestInfo.mobilePhone,
      attributionId: guestInfo.attributionId,
      webClientId: webClientId,
      allowSMSConfirmation: guestInfo.smsOptIn ? "Y" : "N",
      desiredMoveInDate: moment(guestInfo.desiredMoveInDate).format("YYYY-MM-DD"),
      minBedrooms: guestInfo.minBedrooms + "",
      minBathrooms: guestInfo.minBathrooms + "",
      requestSource: "Widget",
      propertyList: [
        {
          tourType: guestInfo.tourType ? guestInfo.tourType : "Tour",
          tourTime: timecell.startTime.toDate(),
          propertyName: timecell.selectedResource!.name,
        },
      ],
    };
    return bookingRequest;
  }

  async guestBookings(emailAddress: string): Promise<GuestBookingsResponse> {
    const query = { searchString: emailAddress };
    return axios.post(GUEST_BOOKINGS_URL, query);
  }

  async guestBookingsByRef(bookingRefName: string): Promise<GuestBookingData> {
    const url = `${QUERY_URL}?q=select+id,
            contact__r.firstname,
            contact__r.lastname,
            contact__r.phone,
            contact__r.email,
            tour_start_time__c,
            tour_type__c,
            contact__r.Number_of_Baths__c,
            contact__r.Number_of_Beds__c,
            contact__r.pDesired_Move_In_Date__c,
			contact__r.HasOptedOutOfSMS__c,
            Account__c+from+Tour_Booking__c+WHERE+Name='${bookingRefName}'`;
		return axios.get(url).then((response) => {
			return new Promise<GuestBookingData>((resolve) => {
				resolve(response.data.records[0]);
			});
		});
  }

    async fetchGuestInfoByTourRef(bookingRefName: string): Promise<GuestInfo> {
        const url = `${QUERY_URL}?q=select+id,
                        contact__r.firstname,
                        contact__r.lastname,
                        contact__r.phone,
                        contact__r.email,
						tour_start_time__c,
						tour_type__c,
                        contact__r.Number_of_Baths__c,
                        contact__r.Number_of_Beds__c,
                        contact__r.pDesired_Move_In_Date__c,
                        Account__c+from+Tour_Booking__c+WHERE+Name='${bookingRefName}'`;
        return axios.get<GuestRecordResponse>(url).then((response) => {
        const record: GuestRecord | null =
        response.data.done && response.data.records && response.data.records.length > 0 ? response.data.records[0] : null;
        let moveIn = undefined;
        if (record && record.Contact__r.pDesired_Move_In_Date__c) {
            const today = moment();
            const desiredMoveInDate = moment(record.Contact__r.pDesired_Move_In_Date__c);
			//const tourDate = moment(record.tour_start_time__c);
            if (today.isBefore(desiredMoveInDate)) {
                moveIn = desiredMoveInDate;
            }
        }
        const guestInfo: GuestInfo = {
            firstName:         record?.Contact__r.FirstName,
            lastName:          record?.Contact__r.LastName,
            emailAddress:      record?.Contact__r.Email,
            attributionId:     record?.Lead_Source__c,
            mobilePhone:       record?.Contact__r.Phone,
            smsOptIn:          record ? !record.Contact__r.HasOptedOutOfSMS__c : false,
            desiredMoveInDate: moveIn?.toDate(),
            minBathrooms:      record?.Contact__r.Number_of_Baths__c,
            minBedrooms:       record?.Contact__r.Number_of_Beds__c
        };
        return new Promise<GuestInfo>((resolve) => {
            resolve(guestInfo);
        });
    });
  }

  async bookAppointments(bookingRequest: BookingRequest): Promise<AppointmentsResponse> {
    bookingRequest.requestSource = "Widget";
    return axios.post(BOOKING_URL, bookingRequest);
  }

  async cancelBooking(bookingConfNumber: string): Promise<BookingResponse> {
    return axios.post(CANCEL_BOOKING_URL, bookingConfNumber);
  }

  async cancelAppointments(cancelRequest: AppointmentCancelRequest): Promise<BookingResponse> {
    return axios.post(CANCEL_APPOINTMENTS_URL, cancelRequest);
  }

  async createCalendarBooking(guestInfo: GuestInfo, timecell: TimeCell, webClientId: string): Promise<CalendarBookingResponse> {
    return axios.post(BOOKING_URL, this.bookingRequest(guestInfo, timecell, webClientId)).then((response) => {
      return new CalendarBookingResponse(response.data, timecell);
    });
  }


  async fetchGuestInfo(guestCardId: string): Promise<GuestInfo> {
    const url = `${QUERY_URL}?q=select+id,
                 contact__r.firstname,
                 contact__r.lastname,
                 contact__r.phone,
                 contact__r.email,
                 contact__r.HasOptedOutOfSMS__c,
                 contact__r.pDesired_Move_In_Date__c,
                 Lead_Source__c,
                 RealPage_Customer_ID__c,
                 contact__r.Number_of_Baths__c,
                 contact__r.Number_of_Beds__c,
                 RealPage_Guest_Card_Id__c+from+Neighborhood_Contact__c+WHERE+RealPage_Guest_Card_ID__c
='${guestCardId}'`;
    return axios.get<GuestRecordResponse>(url).then((response) => {
      const record: GuestRecord | null =
        response.data.done && response.data.records && response.data.records.length > 0 ? response.data.records[0] : null;
      //let moveIn = undefined;
      let moveIn = moment(new Date());
      if (record && record.Contact__r.pDesired_Move_In_Date__c) {
        const today = moment();
        const desiredMoveInDate = moment(record.Contact__r.pDesired_Move_In_Date__c);
        if (today.isBefore(desiredMoveInDate)) {
          moveIn = desiredMoveInDate;
        }
      }
      const guestInfo: GuestInfo = {
        firstName: record?.Contact__r.FirstName,
        lastName: record?.Contact__r.LastName,
        emailAddress: record?.Contact__r.Email,
        attributionId: record?.Lead_Source__c,
        mobilePhone: record?.Contact__r.Phone,
        smsOptIn: record ? !record.Contact__r.HasOptedOutOfSMS__c : false,
        desiredMoveInDate: moveIn?.toDate(),
        minBathrooms: record?.Contact__r.Number_of_Baths__c,
        minBedrooms: record?.Contact__r.Number_of_Beds__c,
        //                attributionId: record?.Contact__r.pAttribution_ID__c,
        guestCardId,
      };

      return new Promise<GuestInfo>((resolve) => {
        resolve(guestInfo);
      });
    });
  }
}

export default BookingService;
